/**
 * Cloud File Path Validation Utilities
 *
 * Client-side validation for cloud storage paths.
 * Mirrors backend _validate_odata_path() for defense-in-depth.
 *
 * Note: This is defense-in-depth only. The backend has authoritative
 * validation that rejects all invalid paths. This client-side validation
 * provides:
 * 1. Immediate user feedback
 * 2. Reduced unnecessary round-trips
 * 3. Additional security layer
 */

// Characters that should never appear in cloud paths (matches backend)
const INVALID_PATH_CHARS = [
  "\x00", // Null byte - path truncation attacks
  "\r", // Carriage return - HTTP header injection
  "\n", // Newline - HTTP header injection
  "\t", // Tab - parser confusion
];

// Maximum path depth (matches backend MAX_PATH_DEPTH = 100)
const MAX_PATH_DEPTH = 100;

export interface PathValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Get human-readable name for control characters.
 */
const getCharacterName = (char: string): string => {
  switch (char) {
    case "\x00":
      return "null byte";
    case "\r":
      return "carriage return";
    case "\n":
      return "newline";
    case "\t":
      return "tab";
    default:
      return `character code ${char.charCodeAt(0)}`;
  }
};

/**
 * Validate a cloud storage path.
 *
 * Checks for:
 * - Null/undefined paths
 * - Invalid control characters (null, CR, LF, tab)
 * - Excessive path depth (>100 levels)
 * - Directory traversal attempts (..)
 *
 * @param path - The file or folder path to validate
 * @returns Validation result with error message if invalid
 */
export const validateCloudPath = (path: string): PathValidationResult => {
  // Check for null/undefined
  if (path === null || path === undefined) {
    return { isValid: false, error: "Path is required" };
  }

  // Empty string or "/" is valid (root path)
  if (path === "" || path === "/") {
    return { isValid: true };
  }

  // Check for invalid characters
  for (const char of INVALID_PATH_CHARS) {
    if (path.includes(char)) {
      const charName = getCharacterName(char);
      return {
        isValid: false,
        error: `Path contains invalid character: ${charName}`,
      };
    }
  }

  // Check path depth
  const depth = path.split("/").filter(Boolean).length;
  if (depth > MAX_PATH_DEPTH) {
    return {
      isValid: false,
      error: `Path depth (${depth}) exceeds maximum (${MAX_PATH_DEPTH})`,
    };
  }

  // Check for path traversal attempts
  // Split by / and check for ".." segments
  const segments = path.split("/");
  if (segments.some((seg) => seg === "..")) {
    return {
      isValid: false,
      error: "Path contains directory traversal sequence (..)",
    };
  }

  return { isValid: true };
};

/**
 * Validate and throw error if path is invalid.
 * Use in service functions before API calls.
 *
 * @param path - The path to validate
 * @throws Error if path is invalid
 */
export const assertValidCloudPath = (path: string): void => {
  const result = validateCloudPath(path);
  if (!result.isValid) {
    throw new Error(result.error);
  }
};

/**
 * Check if a filename is valid (no path separators or invalid chars).
 *
 * @param filename - The filename to validate
 * @returns Validation result with error message if invalid
 */
export const validateCloudFilename = (
  filename: string,
): PathValidationResult => {
  if (!filename) {
    return { isValid: false, error: "Filename is required" };
  }

  // Filenames should not contain path separators
  if (filename.includes("/") || filename.includes("\\")) {
    return {
      isValid: false,
      error: "Filename cannot contain path separators",
    };
  }

  // Check for invalid characters
  for (const char of INVALID_PATH_CHARS) {
    if (filename.includes(char)) {
      return {
        isValid: false,
        error: "Filename contains invalid character",
      };
    }
  }

  return { isValid: true };
};
